import {
  CheckOutlined,
  CloseOutlined,
  MoreOutlined,
  RedoOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import {
  DeleteButtonProps,
  ListButtonProps,
  RefreshButton,
  RefreshButtonProps,
} from '@refinedev/antd';
import { Button, ButtonProps, Flex, message, Modal, Tooltip } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import MessageChat from '@/components/general/MessageChat/MessageChat';
import PopupMenu from '@/components/general/PopupMenu';
import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import { useCountryContext } from '@/contexts/CountryContext';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUiContext } from '@/contexts/UiContext';
import { useUserContext } from '@/contexts/UserContext';
import { ENTITY_TYPE } from '@/model/General/MessageChat';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

import {
  getMissingBillingFields,
  hasInvalidServiceOrderItemsDistribution,
} from './serviceOrderShowHelpers';

interface ServiceOrderShowHeaderButtonsProps {
  serviceOrder: ServiceOrder;
  serviceOrderItems: ServiceOrderItem[];
  // Estado de fetching de ESTA orden específicamente (useShow), para no depender
  // del cálculo interno de RefreshButton, que matchea por recurso sin filtrar por id
  // y puede quedar en true por queries de otras órdenes (ver useRefreshButton en refine).
  isRefreshing: boolean;
  refineButtonsProps: {
    deleteButtonProps: DeleteButtonProps | undefined;
    refreshButtonProps: RefreshButtonProps | undefined;
    listButtonProps: ListButtonProps | undefined;
  };
  refetch: () => void;
  modifingAdminData?: boolean;
  /** Bloquea acciones de cambio de estado por mismatch de país */
  blockForCountry?: boolean;
}

const ServiceOrderShowHeaderButtons = (props: ServiceOrderShowHeaderButtonsProps) => {
  const { t } = useTranslation();
  const { serviceOrderRepository } = useRepositoriesContext();
  const { isMediumScreen } = useUiContext();
  const { userWorkArea } = useUserContext();
  const { hasPermission } = usePermissionContext();
  const { isBrasilSelected } = useCountryContext();
  const {
    serviceOrder,
    serviceOrderItems,
    isRefreshing,
    refineButtonsProps,
    refetch,
    modifingAdminData,
    blockForCountry,
  } = props;
  const { status, id } = serviceOrder;
  const { refreshButtonProps, deleteButtonProps } = refineButtonsProps;
  const [modal, contextHolder] = Modal.useModal();
  const [notificationApi, notificationContextHolder] = useNotification();
  // Centraliza el cambio de estado: si el PATCH falla, lo avisa (antes fallaba en
  // silencio porque ningún onOk de los Modal.confirm de abajo capturaba el error) y
  // relanza para que el modal se mantenga abierto y permita reintentar.
  const updateServiceOrderStatus = useCallback(
    async (newStatus: SERVICE_ORDER_STATUS) => {
      try {
        return await serviceOrderRepository.updateServiceOrder({ id, status: newStatus });
      } catch (error) {
        message.error(t('Ocurrió un error al actualizar la orden de servicio'));
        throw error;
      }
    },
    [serviceOrderRepository, id, t],
  );
  // Bloqueo de "Enviar a facturación" si hay elementos enviados con cantidades sin distribuir.
  const hasInvalidItemsDistribution = useMemo(
    () => hasInvalidServiceOrderItemsDistribution(serviceOrderItems),
    [serviceOrderItems],
  );
  const disabledTooltipText = useMemo(
    () =>
      blockForCountry
        ? t('Selecciona el país correcto para modificar este recurso')
        : modifingAdminData
          ? t('Guarda los cambios de la sección de administración antes de cambiar el estado')
          : '',
    [blockForCountry, modifingAdminData, t],
  );
  const changeStateButtonProps: ButtonProps[] = useMemo(() => {
    if (
      userWorkArea == USER_WORK_AREA.CUSTOMER_SERVICE &&
      status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION
    ) {
      return [
        {
          key: 'coordinate',
          type: isMediumScreen ? 'text' : 'primary',
          children: t('Coordinar'),
          icon: <CheckOutlined />,
          onClick: () => {
            if (!serviceOrder.service_date) {
              Modal.error({
                title: t('Fecha de servicio requerida'),
                content: t(
                  "Completá la fecha de servicio en la sección 'Detalles de la orden' antes de coordinar la orden de servicio.",
                ),
              });

              return;
            }

            if (!serviceOrder.contact_data) {
              Modal.error({
                title: t('Contacto requerido'),
                content: t(
                  "Completá el contacto de la empresa en la sección 'Detalles de la orden' antes de coordinar la orden de servicio.",
                ),
              });

              return;
            }

            modal.confirm({
              title: t('¿Estás seguro de que deseas coordinar esta orden de servicio?'),
              onOk: async () => {
                await updateServiceOrderStatus(SERVICE_ORDER_STATUS.COORDINADA);
                refetch();
              },
            });
          },
        },
      ];
    }

    if (
      userWorkArea == USER_WORK_AREA.CUSTOMER_SERVICE &&
      status == SERVICE_ORDER_STATUS.COORDINADA
    ) {
      return [
        {
          key: 'performed',
          type: isMediumScreen ? 'text' : 'primary',
          children: t('Servicio realizado'),
          icon: <CheckOutlined />,
          onClick: () => {
            modal.confirm({
              title: t('¿Estás seguro de que deseas marcar como realizado este servicio?'),
              onOk: async () => {
                await updateServiceOrderStatus(SERVICE_ORDER_STATUS.REALIZADA);
                refetch();
              },
            });
          },
        },
      ];
    }

    if (
      userWorkArea == USER_WORK_AREA.CUSTOMER_SERVICE &&
      status == SERVICE_ORDER_STATUS.REALIZADA
    ) {
      // ARG: el envío a facturación ahora ocurre automáticamente cuando el técnico confirma
      // por el link (ver ServiceOrderShowBilling), por lo que se oculta el botón manual.
      if (!isBrasilSelected) {
        return [];
      }

      return [
        {
          key: 'sendToBilling',
          type: isMediumScreen ? 'text' : 'primary',
          children: t('Enviar a pago'),
          icon: <CheckOutlined />,
          onClick: () => {
            // BR: validar NF si hay pago al técnico o cobro al cliente
            if (
              (serviceOrder.technician_pay || serviceOrder.customer_invoice) &&
              !serviceOrder.bill_file
            ) {
              Modal.error({
                title: t('Nota Fiscal requerida'),
                content: t(
                  "Debe cargar la Nota Fiscal en la sección 'Nota Fiscal' antes de enviar a pago.",
                ),
              });

              return;
            }

            modal.confirm({
              title: t('¿Estás seguro de que deseas enviar la orden a pago?'),
              onOk: () => {
                void updateServiceOrderStatus(SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION)
                  .then(serviceOrder => {
                    const receptionsCreated = serviceOrder?.receptions_created ?? [];

                    if (receptionsCreated.length > 0) {
                      notificationApi.success({
                        message:
                          receptionsCreated.length > 1
                            ? t('Se crearon {{count}} recepciones asociadas al envío', {
                                count: receptionsCreated.length,
                              })
                            : t('Se ha creado la recepción asociada al envío'),
                        description: (
                          <Flex vertical gap={2}>
                            <span>
                              {receptionsCreated.length > 1
                                ? t('Seleccione cuál recepción desea abrir')
                                : t('Haga click para abrir la recepción en un nuevo enlace')}
                            </span>
                            {receptionsCreated.map((receptionId, index) => (
                              <Button
                                key={receptionId}
                                type="link"
                                style={{ padding: 0, height: 'auto' }}
                                onClick={() => {
                                  window.open(`/v2/receptions/show/${receptionId}`, '_blank');
                                }}>
                                {`${t('Recepción')} ${index + 1} - #${receptionId}`}
                              </Button>
                            ))}
                          </Flex>
                        ),
                      });
                    }

                    refetch();
                  })
                  // El error ya se avisó dentro de updateServiceOrderStatus; solo evita el
                  // unhandled rejection, ya que este onOk no es async y no lo propaga.
                  .catch(() => undefined);
              },
            });
          },
        },
      ];
    }

    if (userWorkArea != USER_WORK_AREA.ADMINISTRATION && userWorkArea != USER_WORK_AREA.SOFTWARE) {
      return [];
    }

    // Brasil: botón de marcar como pagado (avanza a FINALIZADA)
    if (isBrasilSelected && status == SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION) {
      return [
        {
          key: 'backToFinished',
          type: isMediumScreen ? 'text' : 'default',
          children: t('Volver a realizada'),
          icon: <RollbackOutlined />,
          onClick: () => {
            modal.confirm({
              title: t('¿Estás seguro de que deseas volver esta orden al estado de realizada?'),
              onOk: async () => {
                await updateServiceOrderStatus(SERVICE_ORDER_STATUS.REALIZADA);
                refetch();
              },
            });
          },
        },
        {
          key: 'markPaid',
          type: isMediumScreen ? 'text' : 'primary',
          children: t('Marcar como pagado'),
          icon: <CheckOutlined />,
          onClick: () => {
            modal.confirm({
              title: t('¿Marcar como pagado?'),
              content: t('La orden pasará al estado Finalizada'),
              okText: t('Confirmar'),
              cancelText: t('Cancelar'),
              onOk: async () => {
                await updateServiceOrderStatus(SERVICE_ORDER_STATUS.FINALIZADA);
                refetch();
              },
            });
          },
        },
      ];
    }

    return status == SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION
      ? [
          {
            key: 'backToFinished',
            type: isMediumScreen ? 'text' : 'default',
            children: t('Volver a realizada'),
            icon: <RollbackOutlined />,
            onClick: () => {
              modal.confirm({
                title: t('¿Estás seguro de que deseas volver esta orden al estado de realizada?'),
                onOk: async () => {
                  await updateServiceOrderStatus(SERVICE_ORDER_STATUS.REALIZADA);
                  refetch();
                },
              });
            },
          },
          {
            key: 'complete',
            type: isMediumScreen ? 'text' : 'primary',
            children: t('Finalizar'),
            icon: <CheckOutlined />,
            onClick: () => {
              modal.confirm({
                title: t('¿Estás seguro de que deseas finalizar la orden de servicio?'),
                content: t(
                  'Una vez finalizada, no se podrán realizar más modificaciones a esta orden de servicio',
                ),
                onOk: async () => {
                  await updateServiceOrderStatus(SERVICE_ORDER_STATUS.FINALIZADA);
                  refetch();
                },
              });
            },
          },
        ]
      : [];
  }, [
    userWorkArea,
    status,
    isMediumScreen,
    t,
    modal,
    updateServiceOrderStatus,
    refetch,
    notificationApi,
    isBrasilSelected,
    serviceOrder,
  ]);
  // Bloqueo de "Volver a realizada"/"Marcar como pagado"/"Finalizar" si faltan datos administrativos
  const nonPaymentData: { tooltip: string; disabled: boolean } = useMemo(
    () =>
      serviceOrder.hours_to_pay == undefined || serviceOrder.kilometres_to_pay == undefined
        ? { tooltip: t('Faltan datos de facturación'), disabled: true }
        : { tooltip: '', disabled: false },
    [serviceOrder.hours_to_pay, serviceOrder.kilometres_to_pay, t],
  );
  // Bloqueo de "Enviar a facturación" si faltan datos de la sección "Detalles de la facturación"
  const missingBillingFields = useMemo(
    () => getMissingBillingFields(serviceOrder, isBrasilSelected),
    [serviceOrder, isBrasilSelected],
  );
  // Renderizado de botones de cambio de estado con tooltip y disabled condicional.
  const changeStateButtons = useMemo(
    () =>
      changeStateButtonProps.map((buttonProps: any) => {
        const { key, ...restProps } = buttonProps;
        // Tooltip específico para markPaid sin NF
        const markPaidMissingNF = key == 'markPaid' && !serviceOrder.bill_file;
        const sendToBillingMissingData = key == 'sendToBilling' && missingBillingFields.length > 0;
        const sendToBillingInvalidItems = key == 'sendToBilling' && hasInvalidItemsDistribution;
        // Estas transiciones no dependen de los datos administrativos (horas/km a
        // pagar): coordinate y performed ocurren antes de que esos datos existan;
        // backToFinished/sendToBilling/markPaid tienen su propia validación.
        const exemptFromPaymentDataCheck =
          key == 'backToFinished' ||
          key == 'sendToBilling' ||
          key == 'markPaid' ||
          key == 'coordinate' ||
          key == 'performed';
        const tooltipText =
          key == 'backToFinished'
            ? ''
            : markPaidMissingNF
              ? t('Debe existir una Nota Fiscal cargada para marcar como pagado')
              : sendToBillingMissingData
                ? t(
                    isBrasilSelected
                      ? "Completá los siguientes datos en la sección 'Detalles de la facturación' antes de enviar a pago: {{fields}}."
                      : "Completá los siguientes datos en la sección 'Detalles de la facturación' antes de enviar a facturación: {{fields}}.",
                    { fields: missingBillingFields.map(field => t(field)).join(', ') },
                  )
                : sendToBillingInvalidItems
                  ? t(
                      isBrasilSelected
                        ? "Completá las cantidades de los elementos enviados en la sección 'Elementos utilizados' antes de enviar a pago"
                        : "Completá las cantidades de los elementos enviados en la sección 'Elementos utilizados' antes de enviar a facturación",
                    )
                  : exemptFromPaymentDataCheck
                    ? disabledTooltipText
                    : disabledTooltipText || nonPaymentData.tooltip;

        return (
          <Tooltip key={key} title={tooltipText}>
            <Button
              {...restProps}
              disabled={
                blockForCountry ||
                markPaidMissingNF ||
                sendToBillingMissingData ||
                sendToBillingInvalidItems ||
                (exemptFromPaymentDataCheck
                  ? modifingAdminData
                  : modifingAdminData || nonPaymentData.disabled)
              }
            />
          </Tooltip>
        );
      }),
    [
      blockForCountry,
      changeStateButtonProps,
      disabledTooltipText,
      hasInvalidItemsDistribution,
      isBrasilSelected,
      missingBillingFields,
      modifingAdminData,
      nonPaymentData.disabled,
      nonPaymentData.tooltip,
      serviceOrder.bill_file,
      t,
    ],
  );
  const cancelButton = useMemo(
    () =>
      hasPermission('edit') &&
      !blockForCountry &&
      (status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION ||
        status == SERVICE_ORDER_STATUS.COORDINADA) ? (
        <Tooltip title={disabledTooltipText}>
          <Button
            key={'cancel'}
            disabled={modifingAdminData}
            danger
            icon={<CloseOutlined />}
            type={isMediumScreen ? 'text' : 'default'}
            onClick={() => {
              modal.confirm({
                title: t('¿Estás seguro de que deseas cancelar la orden de servicio?'),
                onOk: async () => {
                  await updateServiceOrderStatus(SERVICE_ORDER_STATUS.CANCELADO);
                  refetch();
                },
              });
            }}>
            {t('Cancelar')}
          </Button>
        </Tooltip>
      ) : null,
    [
      disabledTooltipText,
      hasPermission,
      isMediumScreen,
      modal,
      modifingAdminData,
      refetch,
      updateServiceOrderStatus,
      status,
      t,
      blockForCountry,
    ],
  );
  const popupMenuContent: ReactNode[] = useMemo(() => {
    if (!hasPermission('edit') && !hasPermission('delete')) {
      return [];
    }

    const content: ReactNode[] = [];

    if (isMediumScreen && changeStateButtons.length > 0) {
      content.push(...changeStateButtons);
    }

    if (isMediumScreen && cancelButton) {
      content.push(cancelButton);
    }

    if (
      status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION ||
      status == SERVICE_ORDER_STATUS.COORDINADA
    ) {
      content.push(
        <ProtectedDeleteButton
          key="delete"
          {...deleteButtonProps}
          type="text"
          disabled={blockForCountry}
        />,
      );
    }

    return content;
  }, [
    cancelButton,
    changeStateButtons,
    deleteButtonProps,
    hasPermission,
    isMediumScreen,
    status,
    blockForCountry,
  ]);

  return (
    <Flex gap={8} className="ServiceOrderShowHeaderButtons">
      <RefreshButton
        {...refreshButtonProps}
        hideText
        icon={<RedoOutlined spin={isRefreshing} />}
        size={isMediumScreen ? 'small' : 'middle'}
      />
      {!isMediumScreen && cancelButton}
      {!isMediumScreen && changeStateButtons}
      {popupMenuContent.length > 0 && (
        <PopupMenu
          openButton={
            <Tooltip title={disabledTooltipText}>
              <Button
                disabled={modifingAdminData}
                icon={<MoreOutlined />}
                size={isMediumScreen ? 'small' : 'middle'}
              />
            </Tooltip>
          }
          menuContent={
            <Flex vertical align="start" gap={8}>
              {popupMenuContent}
            </Flex>
          }
        />
      )}
      {contextHolder}
      {notificationContextHolder}
      <MessageChat
        entityType={ENTITY_TYPE.ORDEN_DE_SERVICIO}
        entityId={id}
        title={t('Mensajes de la orden de servicio')}
      />
    </Flex>
  );
};

export default ServiceOrderShowHeaderButtons;
